import { useCallback, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Form, Formik, FormikProps } from "formik"
import * as Yup from "yup"
import { Check, Sliders, Layers, Palette, Cpu } from "lucide-react"
import { Integration } from "../../types"
import { useAppSelector, useToast } from "../../common/hooks"
import { selectUser } from "../../redux/slices/auth"
import { useSaveIntegrationMutation } from "../../redux/queries"
import { normalizeUrl, classNames } from "../../common/utils"
import {
  REGEX_HEX_COLOR,
  REGEX_HTPPS_URL_WILDCARD_QUERY,
  ALLOWED_ROLES_BY_ROUTE
} from "../../common/constants"
import { RequireRole } from "../../common/validation"
import { DefaultBtn, PrimaryBtn } from "../../common/ui/buttons"
import Input from "../../common/ui/form/input"
import {
  EmbedsOptions,
  WidgetsCheckboxes,
  Colors
} from "../../common/ui/integrations/fields"
import { IntegrationContext } from "../../common/ui/integrations/manage"
import InstallStep from "../../common/ui/integrations/tabs/install-step"
import { buildIntegrationPayload } from "./utils"

const STEPS = [
  { id: "details", label: "Embed details" },
  { id: "configure", label: "Configure" },
  { id: "install", label: "Install" }
]

const hexColor = Yup.string().matches(
  REGEX_HEX_COLOR,
  "Is not a valid hex color"
)

const step1Schema = Yup.object().shape({
  title: Yup.string().required().min(1),
  url: Yup.string()
    .transform(normalizeUrl)
    .matches(REGEX_HTPPS_URL_WILDCARD_QUERY, "Invalid URL")
    .required(),
  type: Yup.string().required(),
  owner: Yup.string().required()
})

const step2Schema = Yup.object().shape({
  accent_color: hexColor,
  secondary_accent_color: hexColor
})

interface StepIndicatorProps {
  currentStep: number
}

const StepIndicator = ({ currentStep }: StepIndicatorProps) => (
  <nav aria-label="Progress" className="flex items-start justify-center py-6">
    {STEPS.map((step, idx) => {
      const isComplete = idx < currentStep
      const isCurrent = idx === currentStep
      const isUpcoming = idx > currentStep
      return (
        <div key={step.id} className="flex items-start">
          {idx > 0 && (
            <div
              className={classNames(
                "h-0.5 w-8 sm:w-24 lg:w-40 mt-[17px] shrink-0",
                isComplete ? "bg-primary-500" : "bg-neutral-200"
              )}
            />
          )}
          <div className="flex w-24 sm:min-w-28 sm:w-auto flex-col items-center">
            <div
              className={classNames(
                "flex items-center justify-center rounded-full text-sm sm:text-base font-medium",
                "size-[34px] shrink-0",
                isComplete
                  ? "bg-primary-500 text-white"
                  : isCurrent
                  ? "bg-primary-500 text-white"
                  : "border-2 border-neutral-300 bg-white text-neutral-700"
              )}
            >
              {isComplete ? <Check size={16} strokeWidth={3} /> : idx + 1}
            </div>
            <div className="mt-1.5 flex flex-col items-center text-center">
              <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-neutral-400">
                Step {idx + 1}
              </span>
              <span
                className={classNames(
                  "text-xs sm:text-base font-medium",
                  isUpcoming ? "text-neutral-400" : "text-neutral-800"
                )}
              >
                {step.label}
              </span>
            </div>
          </div>
        </div>
      )
    })}
  </nav>
)

interface SectionCardProps {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}

const SectionCard = ({ icon, title, children }: SectionCardProps) => (
  <div className="bg-white rounded shadow-sm p-6">
    <div className="flex items-center gap-2 mb-5">
      <span className="text-violet-400">{icon}</span>
      <span className="text-sm font-medium text-neutral-800">{title}</span>
    </div>
    {children}
  </div>
)

const IntegrationNewPage = () => {
  const navigate = useNavigate()
  const { pushSuccessToast, pushErrorToast } = useToast()
  const user = useAppSelector(selectUser)

  const formRef = useRef<FormikProps<Integration>>(null)
  const [step, setStep] = useState(0)
  const [createdIntegrationId, setCreatedIntegrationId] = useState<
    string | undefined
  >(undefined)

  const [saveIntegration, { isLoading: isSaving }] =
    useSaveIntegrationMutation()

  const initialValues: Integration = useMemo(
    () => ({
      id: "",
      title: "",
      type: "standalone",
      app_key: "",
      owner: user?.user.id ?? "",
      url: "",
      brand_id: "",
      domains: [],
      app_settings: {
        mort_calc: 0,
        afford_calc: 0,
        pricing: 0,
        lead_workflow: 0,
        pricing_set_id: 0,
        settings: {
          aboutBoxes: false,
          loReviews: false,
          brandReviews: false,
          rateTable: false,
          ga_mode: "",
          ga_tag: "",
          contactForm: 0,
          expose_api: true,
          disableCalcCards: false,
          billing_exempt: false,
          postUrl: "",
          accent_color: "",
          secondary_accent_color: ""
        }
      }
    }),
    [user?.user.id]
  )

  const validateStep = useCallback(async () => {
    if (!formRef.current) return false
    const values = formRef.current.values
    try {
      if (step === 0) {
        await step1Schema.validate(values, { abortEarly: false })
      } else if (step === 1) {
        await step2Schema.validate(values, { abortEarly: false })
      }
      return true
    } catch {
      formRef.current.setTouched({
        title: true,
        url: true,
        type: true,
        owner: true,
        accent_color: true,
        secondary_accent_color: true
      })
      await formRef.current.validateForm()
      return false
    }
  }, [step])

  const handleNext = useCallback(async () => {
    const valid = await validateStep()
    if (valid) setStep(s => s + 1)
  }, [validateStep])

  const handleBack = useCallback(() => {
    setStep(s => Math.max(0, s - 1))
  }, [])

  const handleCancel = useCallback(() => {
    navigate("/integrations")
  }, [navigate])

  const handleCreate = useCallback(async () => {
    if (!formRef.current) return
    const valid = await validateStep()
    if (!valid) return

    const values = formRef.current.values
    const postValues = buildIntegrationPayload(values)
    delete postValues.id

    try {
      const result = await saveIntegration(postValues).unwrap()
      pushSuccessToast("Integration has been created successfully")
      setCreatedIntegrationId(
        "integration_id" in result ? result.integration_id : undefined
      )
      setStep(2)
    } catch {
      pushErrorToast("Failed to create integration")
    }
  }, [validateStep, saveIntegration, pushSuccessToast, pushErrorToast])

  const handleClose = useCallback(() => {
    navigate("/integrations")
  }, [navigate])

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.integrations}>
      <div className="min-h-screen bg-[#F1F3FA]">
        <div className="bg-white border-b border-neutral-200 px-6 py-5">
          <h1 className="font-serif text-2xl text-neutral-900">
            Add New Integration
          </h1>
        </div>

        <div className="bg-white mx-6 mt-5 rounded-md">
          <StepIndicator currentStep={step} />
        </div>

        <IntegrationContext.Provider value={{ formIntegrationRef: formRef }}>
          <Formik
            initialValues={initialValues}
            onSubmit={handleCreate}
            innerRef={formRef}
            validationSchema={step === 0 ? step1Schema : step2Schema}
            validateOnBlur
            enableReinitialize={false}
          >
            <Form>
              <div className="px-6 pb-6 mt-5">
                {step === 0 && (
                  <div className="flex flex-col gap-5">
                    <SectionCard
                      icon={<Sliders size={15} />}
                      title="General Settings"
                    >
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <Input
                          label="Integration Title"
                          name="title"
                          labelClassName="text-neutral-400 text-xs uppercase"
                        />
                        <Input
                          label="Destination URL"
                          name="url"
                          placeholder="Ex. https://google.com/*"
                          labelClassName="text-neutral-400 text-xs uppercase"
                        />
                      </div>
                    </SectionCard>

                    <SectionCard
                      icon={<Layers size={15} />}
                      title="Choose Embed Type"
                    >
                      <EmbedsOptions />
                    </SectionCard>
                  </div>
                )}

                {step === 1 && (
                  <div className="flex flex-col gap-5">
                    <SectionCard
                      icon={<Cpu size={15} />}
                      title="Select the embed(s) you want to activate"
                    >
                      <WidgetsCheckboxes />
                    </SectionCard>

                    <SectionCard icon={<Palette size={15} />} title="Branding">
                      <Colors />
                    </SectionCard>
                  </div>
                )}

                {step === 2 && (
                  <InstallStep integrationId={createdIntegrationId} />
                )}

                <div className="flex justify-end gap-3 mt-6">
                  {step === 0 && (
                    <>
                      <DefaultBtn onClick={handleCancel}>Cancel</DefaultBtn>
                      <PrimaryBtn onClick={handleNext}>Continue</PrimaryBtn>
                    </>
                  )}
                  {step === 1 && (
                    <>
                      <DefaultBtn onClick={handleBack}>Back</DefaultBtn>
                      <DefaultBtn onClick={handleCancel}>Cancel</DefaultBtn>
                      <PrimaryBtn onClick={handleCreate} isLoading={isSaving}>
                        Create Integration
                      </PrimaryBtn>
                    </>
                  )}
                  {step === 2 && (
                    <PrimaryBtn onClick={handleClose}>Close</PrimaryBtn>
                  )}
                </div>
              </div>
            </Form>
          </Formik>
        </IntegrationContext.Provider>
      </div>
    </RequireRole>
  )
}

export default IntegrationNewPage
